import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Bottom-centre banner that surfaces when the browser reports it has
 * dropped offline. Hidden whenever `navigator.onLine === true`.
 *
 * Canonical per `sustevo-handbook/brand/APP_SHELL.md` § 10 (overlay
 * & utility surfaces — mandatory). Lives at the bottom-centre zone
 * (`fixed bottom-4 left-1/2 -translate-x-1/2`) at `z-50` per the
 * canonical z-index ladder. Polite live region for assistive tech.
 *
 * Paired with TanStack Query's offline behaviour: cached queries
 * keep rendering, mutations queue or fail per the per-mutation config.
 * The banner is the user's only signal that what they see is the
 * last sync.
 */
export function ConnectionStatusBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const [online, setOnline] = useState<boolean>(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = (): void => {
      setOnline(typeof navigator === 'undefined' ? true : navigator.onLine);
    };
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  if (online) return null;

  return (
    <output
      aria-live="polite"
      data-testid="connection-status-banner"
      className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-md bg-[var(--color-ink-900)] px-4 py-2 text-sm text-white shadow-lg"
    >
      {t('system.offline.banner', {
        defaultValue: "You're offline. Showing the last cached data.",
      })}
    </output>
  );
}
